import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Megaphone, BookOpen, CalendarDays, Upload, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Content tab — three stacked publisher cards: announcements, resources
// (upload any image/PDF), and a lightweight event-moderation list that shows
// every non-seed event so admins can nuke spam quickly.
export default function ContentTab({
  announcements, resources, events,
  ann, setAnn, postAnnouncement,
  res, setRes, resFile, postResource,
  load,
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="content-tab">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 shadow-sm">
        <h3 className="font-heading font-bold text-forest mb-4 flex items-center gap-2"><Megaphone className="w-5 h-5 text-terracotta-dark" /> New announcement</h3>
        <div className="space-y-3">
          <Input value={ann.title} onChange={(e) => setAnn({ ...ann, title: e.target.value })} placeholder="Title" className="rounded-xl bg-softstone/60 border-sage/30" data-testid="ann-title-input" />
          <Textarea value={ann.body} onChange={(e) => setAnn({ ...ann, body: e.target.value })} placeholder="Announcement text..." rows={3} className="rounded-xl bg-softstone/60 border-sage/30" data-testid="ann-body-input" />
          <label className="flex items-center gap-2 text-sm text-forest/70">
            <Switch checked={ann.pinned} onCheckedChange={(v) => setAnn({ ...ann, pinned: v })} data-testid="ann-pinned-switch" /> Pin to top
          </label>
          <Button onClick={postAnnouncement} disabled={!ann.title || !ann.body} className="rounded-full bg-sage hover:bg-sage-dark text-white" data-testid="ann-submit-btn">Post announcement</Button>
        </div>
        <div className="mt-6 space-y-2">
          {announcements.map((a) => (
            <div key={a.announcement_id} className="flex items-center gap-3 bg-softstone/50 rounded-xl p-3 text-sm" data-testid={`admin-ann-${a.announcement_id}`}>
              <span className="font-semibold text-forest truncate">{a.title}</span>
              <button onClick={async () => { await api.delete(`/announcements/${a.announcement_id}`); load(); }} className="ml-auto p-1.5 text-terracotta-dark hover:bg-terracotta-light rounded-lg" data-testid={`delete-ann-${a.announcement_id}`} aria-label="Delete announcement">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 shadow-sm">
          <h3 className="font-heading font-bold text-forest mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5 text-sage-dark" /> New resource</h3>
          <div className="space-y-3">
            <Input value={res.title} onChange={(e) => setRes({ ...res, title: e.target.value })} placeholder="Title" className="rounded-xl bg-softstone/60 border-sage/30" data-testid="res-title-input" />
            <Input value={res.description} onChange={(e) => setRes({ ...res, description: e.target.value })} placeholder="Short description" className="rounded-xl bg-softstone/60 border-sage/30" data-testid="res-desc-input" />
            <div className="flex gap-3 items-center">
              <Select value={res.kind} onValueChange={(v) => setRes({ ...res, kind: v, url: v === "link" ? (res.url || "") : "" })}>
                <SelectTrigger className="rounded-xl bg-softstone/60 border-sage/30 w-40" data-testid="res-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="printable">Printable</SelectItem>
                  <SelectItem value="schedule">Schedule</SelectItem>
                  <SelectItem value="guide">Guide</SelectItem>
                  <SelectItem value="link">Link</SelectItem>
                </SelectContent>
              </Select>
              {res.kind === "link" ? (
                <Input
                  value={res.url || ""}
                  onChange={(e) => setRes({ ...res, url: e.target.value })}
                  placeholder="https://example.com"
                  type="url"
                  className="rounded-xl bg-softstone/60 border-sage/30 flex-1"
                  data-testid="res-url-input"
                />
              ) : (
                <input ref={resFile} type="file" accept="image/*,application/pdf" className="text-xs text-forest/60" data-testid="res-file-input" />
              )}
            </div>
            <Button onClick={postResource} disabled={!res.title} className="rounded-full bg-sage hover:bg-sage-dark text-white" data-testid="res-submit-btn">
              <Upload className="w-4 h-4 mr-2" /> Publish resource
            </Button>
          </div>
          <div className="mt-6 space-y-2">
            {resources.map((r) => (
              <div key={r.resource_id} className="flex items-center gap-3 bg-softstone/50 rounded-xl p-3 text-sm" data-testid={`admin-res-${r.resource_id}`}>
                <span className="font-semibold text-forest truncate">{r.title}</span>
                <span className="text-[10px] font-bold uppercase text-forest/40">{r.kind}</span>
                <button onClick={async () => { await api.delete(`/resources/${r.resource_id}`); load(); }} className="ml-auto p-1.5 text-terracotta-dark hover:bg-terracotta-light rounded-lg" data-testid={`delete-res-${r.resource_id}`} aria-label="Delete resource">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 shadow-sm">
          <h3 className="font-heading font-bold text-forest mb-4 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-sage-dark" /> Event moderation</h3>
          <div className="space-y-2">
            {events.filter((e) => !(e.event_id || "").startsWith("evt_seed")).map((e) => (
              <div key={e.event_id} className="flex items-center gap-3 bg-softstone/50 rounded-xl p-3 text-sm" data-testid={`admin-event-${e.event_id}`}>
                <span className="font-semibold text-forest truncate">{e.title}</span>
                <span className="text-xs text-forest/50 shrink-0">{e.date}</span>
                <button onClick={async () => { await api.delete(`/events/${e.event_id}`); load(); toast.success("Event removed"); }} className="ml-auto p-1.5 text-terracotta-dark hover:bg-terracotta-light rounded-lg" data-testid={`delete-event-admin-${e.event_id}`} aria-label="Delete event">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
